import { useCallback } from 'react';
import { create } from 'zustand';

/**
 * Anchor elements published by the components that own them.
 *
 * `Tour` renders in a portal at the root, so it has no render relationship to the panel an
 * anchor lives in: when that panel mounts or unmounts, nothing re-renders `Tour` and a plain
 * `getElementById` during its render stays stale. Registering from the owner turns that into a
 * subscription, so a coachmark appears as soon as its anchor mounts and hides when it leaves.
 */
interface TourAnchorsState {
  anchors: Record<string, HTMLElement>;
  setAnchor: (anchorId: string, element: HTMLElement | null) => void;
}

const useTourAnchorsStore = create<TourAnchorsState>((set) => ({
  anchors: {},
  setAnchor: (anchorId, element) =>
    set((state) => {
      if (element === null) {
        if (!(anchorId in state.anchors)) return state;
        const { [anchorId]: _unmounted, ...remaining } = state.anchors;
        return { anchors: remaining };
      }
      if (state.anchors[anchorId] === element) return state;
      return { anchors: { ...state.anchors, [anchorId]: element } };
    }),
}));

/** Callback ref for an element that tour steps anchor to. Pass `undefined` to opt out. */
export function useTourAnchorRef(anchorId: string | undefined) {
  const setAnchor = useTourAnchorsStore((state) => state.setAnchor);

  return useCallback(
    (element: HTMLElement | null) => {
      if (!anchorId) return;
      setAnchor(anchorId, element);
    },
    [anchorId, setAnchor],
  );
}

/** The registered element for `anchorId`, re-rendering the caller when it mounts or unmounts. */
export function useRegisteredTourAnchor(anchorId: string | undefined): HTMLElement | null {
  return useTourAnchorsStore((state) => (anchorId ? (state.anchors[anchorId] ?? null) : null));
}
